import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Lightbulb, Check, X, Trash2, RefreshCw, Star, Image } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_sip-schedule-admin/artifacts/vd09zrou_Logo%20-%20Final.png";

const StarRating = ({ rating, totalRatings }) => {
  return (
    <div className="flex items-center gap-1">
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={14}
            className={rating >= star ? 'fill-sip-yellow text-sip-yellow' : 'fill-gray-200 text-gray-300'}
          />
        ))}
      </div>
      {totalRatings && (
        <span className="text-xs text-gray-500">
          {typeof rating === 'number' ? rating.toFixed(1) : rating} ({totalRatings})
        </span>
      )}
    </div>
  );
};

export const AdminFlavors = () => {
  const [flavors, setFlavors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'pending', 'approved'

  const fetchFlavors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/admin/flavors`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFlavors(data);
      } else {
        toast.error('Failed to fetch flavor submissions');
      }
    } catch (error) {
      console.error('Error fetching flavors:', error);
      toast.error('Failed to load flavor submissions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchFlavors();
  }, [fetchFlavors]);

  const handleApprove = async (flavorId) => {
    try {
      const response = await fetch(`${API}/admin/flavors/${flavorId}/approve`, {
        method: 'PUT',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Flavor approved!');
        setFlavors(flavors.map(f => 
          f.flavor_id === flavorId ? { ...f, is_approved: true } : f
        ));
      } else {
        toast.error('Failed to approve flavor');
      }
    } catch (error) {
      console.error('Error approving flavor:', error);
      toast.error('Failed to approve flavor');
    }
  };

  const handleDelete = async (flavorId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      const response = await fetch(`${API}/admin/flavors/${flavorId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      if (response.ok) {
        toast.success('Flavor submission deleted');
        setFlavors(flavors.filter(f => f.flavor_id !== flavorId));
      } else {
        toast.error('Failed to delete flavor');
      }
    } catch (error) {
      console.error('Error deleting flavor:', error);
      toast.error('Failed to delete flavor');
    }
  };

  const filteredFlavors = flavors.filter(flavor => {
    if (filter === 'pending') return !flavor.is_approved;
    if (filter === 'approved') return flavor.is_approved;
    return true;
  });

  const pendingCount = flavors.filter(f => !f.is_approved).length;
  const approvedCount = flavors.filter(f => f.is_approved).length;

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-flavors-page">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-gray-600 hover:text-sip-purple transition-colors">
                <ArrowLeft size={24} />
              </Link>
              <Link to="/">
                <img src={LOGO_URL} alt="Sip Fantasia" className="h-10 w-auto" />
              </Link>
            </div>
            <h1 className="font-script text-xl md:text-2xl text-gray-900">Flavor Submissions</h1>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats & Filter */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Lightbulb className="w-8 h-8 text-sip-orange" />
            <div>
              <h2 className="font-script text-2xl md:text-3xl text-gray-900">Community Ideas</h2>
              <p className="text-gray-600 text-sm">Manage flavor submissions from customers</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={fetchFlavors}
              className="p-2 text-gray-600 hover:text-sip-purple transition-colors"
              title="Refresh"
              data-testid="refresh-flavors-btn"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-full font-medium transition-colors ${
              filter === 'all' 
                ? 'bg-sip-purple text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            data-testid="filter-all"
          >
            All ({flavors.length})
          </button>
          <button
            onClick={() => setFilter('pending')}
            className={`px-4 py-2 rounded-full font-medium transition-colors ${
              filter === 'pending' 
                ? 'bg-sip-orange text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            data-testid="filter-pending"
          >
            Pending ({pendingCount})
          </button>
          <button
            onClick={() => setFilter('approved')}
            className={`px-4 py-2 rounded-full font-medium transition-colors ${
              filter === 'approved' 
                ? 'bg-sip-cyan text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
            data-testid="filter-approved"
          >
            Approved ({approvedCount})
          </button>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin w-8 h-8 border-4 border-sip-purple border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-gray-600">Loading submissions...</p>
          </div>
        ) : filteredFlavors.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-2xl">
            <Lightbulb className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">No Submissions Yet</h3>
            <p className="text-gray-500">
              {filter === 'pending' 
                ? 'No pending submissions to review' 
                : filter === 'approved'
                ? 'No approved submissions yet'
                : 'Customer flavor ideas will appear here'}
            </p>
          </div>
        ) : (
          <div className="grid gap-4">
            {filteredFlavors.map((flavor, index) => (
              <motion.div
                key={flavor.flavor_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className={`bg-white rounded-2xl shadow-sm border-l-4 overflow-hidden ${
                  flavor.is_approved ? 'border-sip-cyan' : 'border-sip-orange'
                }`}
                data-testid={`flavor-card-${flavor.flavor_id}`}
              >
                <div className="flex flex-col md:flex-row">
                  {/* Image thumbnail if exists */}
                  {flavor.image_url && (
                    <div className="md:w-48 h-40 md:h-auto shrink-0">
                      <img 
                        src={flavor.image_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${flavor.image_url}` : flavor.image_url}
                        alt={flavor.flavor_combo}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  <div className="flex-1 p-6">
                    <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <span className="font-semibold text-lg text-gray-900">{flavor.name}</span>
                          <StarRating 
                            rating={flavor.average_rating || flavor.rating} 
                            totalRatings={flavor.total_ratings || (flavor.community_ratings?.length || 0) + 1}
                          />
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            flavor.is_approved 
                              ? 'bg-sip-cyan/20 text-sip-cyan' 
                              : 'bg-sip-orange/20 text-sip-orange'
                          }`}>
                            {flavor.is_approved ? 'Approved' : 'Pending'}
                          </span>
                        </div>
                        
                        <div className="mb-3">
                          <span className="text-sm text-gray-500">Flavor Combo:</span>
                          <p className="text-sip-purple font-medium">{flavor.flavor_combo}</p>
                        </div>
                        
                        {flavor.description && (
                          <p className="text-gray-600 text-sm">{flavor.description}</p>
                        )}
                        
                        <p className="text-xs text-gray-400 mt-3">
                          Submitted: {new Date(flavor.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        {!flavor.is_approved && (
                          <button
                            onClick={() => handleApprove(flavor.flavor_id)}
                            className="flex items-center gap-2 px-4 py-2 bg-sip-cyan text-white rounded-full hover:bg-sip-cyan/90 transition-colors"
                            data-testid={`approve-btn-${flavor.flavor_id}`}
                          >
                            <Check size={16} />
                            <span className="hidden sm:inline">Approve</span>
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(flavor.flavor_id)}
                          className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                          data-testid={`delete-btn-${flavor.flavor_id}`}
                        >
                          <Trash2 size={16} />
                          <span className="hidden sm:inline">Delete</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminFlavors;
